import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { BreadCrumb, SnackSimple } from '../../../../components'
import { useContextState } from '../../../../hooks/theme'
import { UserService } from '../../../../service'
import { CRUD, Types } from '../../../../utils/enums'
import { InformativeCard, InputModel } from '../../../../utils/types'
import { ObjectGeneric, onChangeEvent, Table } from '../../../../utils/types/generals'
import { formatFilterMainAreas, initialData, initialValues, input, useMenuItems } from './initialState'
import { getAreaList, handleErrors, submit } from './methods'
import { useStyles } from './style'
import Form from './components/form'
import { generalDisabled } from '../../../../utils/validate/validate'
import { httpFetch } from '../../../../utils/crud'
import General from '../../../../components/modules/general'
import { icons } from '../../../../utils/icons'
import { FiltersDrawer } from '../../../../components/filtersDrawer'
import { Box, Grid, Icon, IconButton, Tooltip, Typography } from '@mui/material'
import { DinamicForm } from '../../../../components/dinamicForm'
import { areaService } from '../../../../service/area'
import SimpleModal from '../../../../components/modal/simpleModal'
import { useFilter } from '../../../../hooks/filter'
import { useDownload } from '../../../../hooks/download/useDownload'
import { returnQuery, returnQueryWithPagination } from '../../../../utils/methods/returnQuery'
import { ModelDownloadButtons } from '../../../../components/donwloadFiles/model'
import { areaServices } from '../../../../utils/constants/callService'
import { getPreviousYears, monthYear } from '../../../../utils/constants/date_month'

const Area = () => {
  const { t } = useTranslation()
  const { colors } = useContextState()
  const styles = useStyles(colors)
  const [information, setInformation] = useState<InformativeCard | null>(null)
  const [data, setData] = useState<ObjectGeneric>({})
  const [btnLoading, setBtnLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [allErrors, setAllErrors] = useState<any>({})
  const detailToInclude = ['email', 'phone', 'name', 'createTime', 'responsible']
  const [inputValues, setInputValues] = useState(initialValues)
  const [inputs, setInputs] = useState<{ [x: string]: InputModel }>(input)
  const [openModalDelete, setOpenModalDelete] = useState(false)

  const [row, setRow] = useState<Table['row']>([])
  const refTime = useRef<NodeJS.Timeout | null>(null)
  const filterMain = useFilter({ formatFilter: formatFilterMainAreas })
  const { counter, errors, filterData, paginationModel, handlePagination, onChange, cleanFilter, sortTable, query, filterString } = filterMain
  const [totalPages, setTotalPages] = useState(0)
  const [selected, setSelected] = useState<number>(-1)
  const [open, setOpen] = useState(false)
  const [crud, setCrud] = useState(false)
  const [drawerDetail, setDrawerDetail] = useState({
    title: '',
    accept: '',
    cancel: '',
  })

  const [openSnackbar, setOpenSnackbar] = useState({
    open: false,
    message: '',
    type: Types.SUCCESS,
  })

  const menuList = useMenuItems(styles)
  const [menu, setMenu] = useState<
    {
      label: string
      value?: any
    }[]
  >(menuList)

  const handleCloseSnackbar = (value: boolean) => setOpenSnackbar({ ...openSnackbar, open: value })

  const crudChange = async (e: onChangeEvent | string, extra: any): Promise<void> => {
    if (typeof e !== 'string') {
      const { name, value } = e?.target

      let body = {
        ...data,
        [name]: value?.value ?? value,
      }

      setData((prev) => ({ ...prev, ...body }))
      setAllErrors(handleErrors(body, t))
    }
  }

  const handleSubmit = async () => {
    setBtnLoading(true)
    const response = await submit(data, t, information?.id)
    if (!response?.response?.errors) {
      getAllAreas()
      //cleanFilter()
      getDepartments()
    }
    setOpenSnackbar(response.snackbarBody)
    setBtnLoading(false)
  }

  const handleInformation = (e: any, index: number) => {
    setSelected(index)
    openDrawer(true)

    let data: any[] = []
    setAllErrors({})
    if (e) {
      Object.entries(e ?? {}).forEach(([key, value]: any) => {
        if (!['id', 'department', 'departments'].includes(key)) {
          data = [
            ...data,
            {
              label: t(`general.tags.${key}`),
              value: value?.label ?? value?.text ?? (typeof value === 'string' || !value ? value : null),
              selected: value?.value ?? value,
              name: key,
              copy: key === 'email',
            },
          ]
        }
      })
      setMenu(menuList?.filter((item) => item.value !== e.status?.value))
    }

    setInformation(
      e
        ? {
          data,
          tmpData: data,
          status: {
            color: e.status.color,
            text: e.status.text,
            value: e.status?.value,
          },
          id: e.id,
          isAreaWithConfig: e.isAreaWithConfig,
        }
        : null
    )

    setData(initialData)
  }

  const handleMenu = async (item: any) => {
    if (item.value === CRUD.EDIT) {
      setCrud(true)
      setBtnLoading(true)

      areaService(httpFetch)
        .getAreaById(information?.id as number)
        .then((resp) => {
          let body = {}
          information?.tmpData?.forEach((element) => {
            body = {
              ...body,
              [element.name as string]: element?.selected ?? element.value,
            }
          })

          const { userManage, departments, userManageId } = resp

          setData((prev) => ({
            ...prev,
            ...body,
            responsible: userManageId,
            departments: departments?.map((e: any) => ({
              value: e.id,
              label: e.name,
            })),
          }))

          /* let responsibles = inputValues.responsible.items
          responsibles?.push({ value: resp.userManage?.id, label: `${resp?.userManage?.firstName ?? ''} ${resp?.userManage?.lastName ?? ''}` }) */

          let departmentsList = inputValues.departments.items
          resp?.departments?.forEach((e: any) => departmentsList?.push({ value: e.id, label: e.name }))

          setInputValues((prev) => ({
            ...prev,
            departments: {
              ...prev.departments,
              items: departmentsList,
            },
          }))
        })
        .finally(() => {
          setBtnLoading(false)
        })
    } else if (item.value === CRUD.DELETE) {
      setOpenModalDelete(true)
    }
  }

  const handleDelete = () => {
    if (information?.id) {
      setBtnLoading(true)
      areaService(httpFetch)
        .deleteArea(information.id)
        .then((response: any) => {
          if (!response.errors) {
            getAllAreas()
            getDepartments()
            setOpenSnackbar({
              open: true,
              type: Types.SUCCESS,
              message: t(`area.snackbar.delete.success`),
            })
          } else {
            let obj = {
              open: true,
              type: Types.ERROR,
              message: t(`area.snackbar.delete.error`),
            }
            const { code } = response.errors[0]
            if (code == 'ERR_DELETE_QUESTIONNAIRE_MAIN') obj.message = t(`area.snackbar.delete.errorCode.ERR_DELETE_QUESTIONNAIRE_MAIN`)

            setOpenSnackbar(obj)
          }
        })
        .catch(() => {
          setOpenSnackbar({
            open: true,
            type: Types.ERROR,
            message: t(`area.snackbar.delete.error`),
          })
        })
        .finally(() => {
          setBtnLoading(false)
          setOpenModalDelete(false)
        })
    }
  }

  const getAllAreas = async () => {
    resetValues()
    setIsLoading(true)
    const responseAllAreas = await getAreaList(colors, t,returnQuery({ ...filterMain.query, filter: filterMain.query.filter?.replaceAll('+', '%2B') }))

    const newRow = responseAllAreas.row.map((item) => {

      return {
        ...item,
        department: {
          children:
            item?.departments?.length > 0 ? (
              <Grid container justifyContent="center" alignItems="center">
                <Grid item xl={6} lg={6} md={6} sm={6} xs={6} sx={{ color: '#828282' }}>
                  <Typography sx={styles.textItem}>{item?.departments.length ? <span>{item?.departments[0].name}</span> : null}</Typography>
                </Grid>

                {!!(item?.departments.length && item?.departments.length > 1) && (
                  <Grid sx={styles.containerIconTooltip} item xl={1} lg={1} md={1} sm={1} xs={1}>
                    <Tooltip
                      title={
                        <Box>
                          <Box sx={styles.containerTooltipAsk}>
                            <Box sx={styles.listAnswers}>
                              {item?.departments?.map((itemO: any) => (
                                <Typography sx={styles.textItem}>{itemO?.name}</Typography>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      }
                    >
                      <Icon sx={styles.viewMore}>{icons('add')}</Icon>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Typography sx={styles.bodyText}>{t('general.noData')}</Typography>
            ),
        },
      }
    })

    setRow(newRow)
    setIsLoading(false)
    setTotalPages(responseAllAreas.totalPages)
    // eslint-disable-next-line
  }

  const getDepartments = async () => {
    const responseDepartments = await areaService(httpFetch).getDepartments()
    const responseAllDepartments = await areaService(httpFetch).getAllDepartments()
    const responseUsers = await UserService(httpFetch).usersByStatus({ addUserLogged: true })

    const user = responseUsers?.map((e: any) => ({
      value: e.id,
      label: `${e.curp} - ${e.firstName} ${e.lastName}`,
    }))

    setInputValues((prev) => ({
      ...prev,
      departments: {
        ...prev.departments,
        items: responseDepartments.map((e: any) => ({
          value: e.id,
          label: e.name,
        })),
      },
      responsible: {
        ...prev.responsible,
        items: user,
      },
    }))

    setInputs((prev) => ({
      ...prev,
      states: {
        ...prev.states,
        items: responseAllDepartments.map((e: any) => ({
          value: e.id,
          label: e.name,
        })),
      },
      users: {
        ...prev.users,
        items: user,
      },
    }))
  }

  const resetValues = () => {
    setCrud(false)
    setOpen(false)
    setData(initialData)
    setBtnLoading(false)
    setSelected(-1)
  }
  const openDrawer = (edit?: boolean) => {
    setDrawerDetail({
      title: t(`area.informationCard.${edit ? 'edit' : 'create'}`),
      accept: t(`area.informationCard.buttons.${edit ? 'update' : 'add'}`),
      cancel: t('userType.informationCard.buttons.cancel'),
    })
    if (!edit) {
      setData(initialData)
    }
    setInformation(null)
    setAllErrors({})
    setCrud(!edit)
    setOpen(true)
  }

  const downloadMain = useDownload({
    docName: t('area.area'),
    query: returnQueryWithPagination({ filter: filterString, sort: query?.sort, }).replace('&', ''),
    downloadFuntion: areaServices.downloadFile,
  })

  const column: any[] = [
    {
      id: 'createTime',
      label: t('general.tags.createTime'),
      sortable: true,
      minWidth: 100,
    },
    {
      id: 'name',
      label: t('general.tags.area'),
      sortable: true,
      minWidth: 100,
    },
    {
      id: 'department',
      label: t('general.tags.state'),
      minWidth: 100,
    },
    {
      id: 'email',
      label: t('general.tags.email'),
      sortable: true,
      minWidth: 100,
    },
    {
      id: 'phone',
      label: t('general.tags.number'),
      sortable: true,
      minWidth: 100,
    },
    {
      id: 'status',
      label: t('general.tags.status'),
      minWidth: 100,
    },
  ]

  const closeDrawer = () => {
    setOpen(false)
    setCrud(false)
    setSelected(-1)
  }

  const handleCreate = async () => {
    setBtnLoading(true)
    openDrawer()
    setBtnLoading(false)
  }

  useEffect(() => {
    if (refTime.current) { clearTimeout(refTime.current) }

    refTime.current = setTimeout(() => {
      getAllAreas()
    }, 300)
  }, [filterMain.query.all])

  useEffect(() => {
    getDepartments()
    setInputs((prev) => ({
      ...prev,
      createYear: {
        ...prev.createYear,
        items: getPreviousYears().map((e: any) => ({
          value: e.value,
          label: e.label,
        })),
      },
      month: {
        ...prev.month,
        items: monthYear.map((e: any) => ({
          value: e.value + 1,
          label: e.label,
        })),
      }
    }))
  }, [])

  
  // if (isLoading) return <Loader />
  return (
    <>
      <SnackSimple close={handleCloseSnackbar} open={openSnackbar.open} type={openSnackbar.type} message={openSnackbar.message} />

      <SnackSimple
        close={downloadMain.closeOpenSnakbar}
        open={downloadMain.openSnackbar.open}
        type={downloadMain.openSnackbar.type as Types}
        message={downloadMain.openSnackbar.message}
      />

      <SimpleModal
        loadingButton
        isLoading={btnLoading}
        open={openModalDelete}
        close={() => {
          setOpenModalDelete(false)
        }}
        title={t('area.modal.delete.title')}
        description={information?.isAreaWithConfig ? 'El área posee configuraciones de folio general. Al borrarla se perderán los cambios.' : ''}
        buttonText={t('general.confirm')}
        cancelText={t('general.cancel')}
        color={colors}
        back={() => {
          setOpenModalDelete(false)
        }}
        next={handleDelete}
      />

      <General
        newHeader={<>
          <BreadCrumb
            removeBreadcrumb={['organization-chart']}
            replaceLabels={{ area: 'area-chart' }}
          />
          <Box sx={styles.header}>
            <Typography variant='title_grey_big'>{t('area.area')}</Typography>

            <FiltersDrawer cleanFilter={cleanFilter} count={counter} right={-50} top={-40}>
              <Box sx={styles.section}>
                <DinamicForm errors={errors} input={Object.values(inputs)} values={filterData} onChange={onChange} filter={true} />
              </Box>
            </FiltersDrawer>

            <Box sx={styles.flex}>
              <ModelDownloadButtons
                csv={downloadMain.downloadCSV}
                pdf={downloadMain.downloadPDF}
                word={downloadMain.downloadWORD}
                disableCSV={downloadMain.download.excel}
                disablePDF={downloadMain.download.pdf}
                disableWORD={downloadMain.download.word}
                styleType
              />

              <Tooltip title={t('ToolTips.clearFilters')}>
                <IconButton
                  sx={(styles.clean, (counter || 0) > 0 ? null : styles.hide)}
                  onClick={() => {
                    cleanFilter()
                  }}
                >
                  {icons('clean')}
                </IconButton>
              </Tooltip>

              <Tooltip title={t('ToolTips.create')}>
                <IconButton
                  sx={styles.clean}
                  className='add'
                  onClick={() => {
                    handleCreate()
                  }}
                >
                  {icons('add')}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </>}
        open={open}
        rightDrawer={{
          ...information,
          showImage: false,
          data: information?.data?.filter((item) => detailToInclude.includes(item?.name ?? '')),
          form: <Form btnLoading={btnLoading} data={data} input={Object.values(inputValues)} crudChange={crudChange} allErrors={allErrors} />,
          menuItems: menu,
          crud,
          title: drawerDetail.title,
          accept: {
            title: drawerDetail.accept,
            disabled: generalDisabled(inputValues, data) || btnLoading,
            onClick: handleSubmit,
          },
          cancel: {
            title: drawerDetail.cancel,
            onClick: closeDrawer,
          },
          clickMenu: handleMenu,
        }}
        table={{
          column: column,
          row: row,
          title: t('general.tags.areas'),
          onClick: handleInformation,
          loadingTable: isLoading,
          selected,
          serverSidePagination: {
            page: paginationModel.page + 1,
            size: paginationModel.pageSize,
            totalPages: totalPages,
          },
          handleChange: (page: number, pageSize: number) => {
            handlePagination({ page, pageSize })
          },
          sortable: (field, sort, count) => {
            sortTable(field && sort ? { field: field, sort: sort, count } : undefined)
          },
          order: query?.sort && {
            count: query?.sort?.count,
            key: query?.sort?.field,
            order: query?.sort?.sort,
          },
        }}
      />
    </>
  )
}

export default Area
